import {
  CircleHelpFillIcon,
  ValidationErrorFilledIcon,
  StatusVerifiedFilledIcon,
} from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Text } from '@infinex/ui/components';

import { InfoImage } from './InfoImage';

import { isPlaceholderDappIcon } from '../utils';
import { smartTruncate } from '../utils/smartTruncate';

type AppInfoCardProps = {
  dappName: string | null;
  dappIcon: string | null;
  hostname: string; // e.g. "uniswap.org"
  dappType: 'verified' | 'unverified' | 'malicious';
  showTopIcon?: boolean;
  showBottomIcon?: boolean;
  subText: string | null;
  className?: string;
};

export function AppInfoCard({
  dappName,
  dappIcon,
  hostname,
  dappType,
  showTopIcon = true,
  showBottomIcon = true,
  subText,
  className = '',
}: AppInfoCardProps) {
  const displayHost = hostname || 'This site';
  const verified = dappType === 'verified' && dappName !== null;

  return (
    <div
      className={cn(
        `bg-surface mt-2 flex w-full flex-col items-center justify-center rounded-t-3xl rounded-b-lg`,
        className
      )}
    >
      <div className="relative">
        <InfoImage
          src={dappIcon}
          className={cn(
            !isPlaceholderDappIcon(dappIcon)
              ? ''
              : 'bg-surfaceHover rounded-2xl p-3'
          )}
          size={60}
          emblemPosition="bottom-right"
        />
        {dappType === 'malicious' && showTopIcon && (
          <ValidationErrorFilledIcon
            aria-label="Validation error"
            size="large"
            className="absolute -right-5 -bottom-3"
            fill="critical"
          />
        )}
        {dappType === 'unverified' && showTopIcon && (
          <CircleHelpFillIcon
            className="absolute -top-[2px] -right-3"
            fill="caution"
          />
        )}
      </div>

      {subText !== null && (
        <Text className="px-16 text-center text-xl leading-tight font-semibold">
          <span className="text-emphasis">
            {smartTruncate(dappName || 'Unknown', 12, 'end')} {subText}
          </span>{' '}
        </Text>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <Text className={'text-secondary truncate text-sm font-medium'}>
            {displayHost}
          </Text>
          {verified && showBottomIcon && (
            <StatusVerifiedFilledIcon
              aria-label="Verified site"
              fill="info"
              size="2xsmall"
            />
          )}
          {dappType === 'unverified' && showBottomIcon && (
            <CircleHelpFillIcon
              aria-label="Verified site"
              fill="caution"
              size="2xsmall"
            />
          )}
        </div>
      </div>
    </div>
  );
}
